import {
    Component,
    ComponentFactoryResolver, ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Output, ViewChildren, ViewContainerRef
} from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { isDefined } from 'ionic-angular/util/util';
import { insertComponentDynamic } from '../../../../core/util/insert-component-dynamic';
import { ControlType } from '../../../business-objects/control-type/control-type.model';
import { Control, ControlInlined } from '../../../business-objects/control/control.model';
import { Form } from '../../../business-objects/form/form.model';
import {
    FieldWrapperComponent,
    IFieldWrapperComponentInputs
} from '../../../modules-shared/field-wrapper/field-wrapper.component';
import { VasAppFacade } from '../../../services/vas-app-facade.service';
import { ControlComponent } from '../../control.component';
import { ComplexControl } from '../complex-control.model';

@Component({
    selector: 'complex-field',
    templateUrl: 'complex-control.component.html',
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ComplexControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => ComplexControlComponent), multi: true}
    ]
})
export class ComplexControlComponent extends ControlComponent implements ControlValueAccessor {
    @Input('value') _value = undefined;
    @Input() definition: ComplexControl;
    @Input() form: Form;
    @Input() control: FormArray;
    @Input() formGroup: FormGroup;
    @Output() changes: EventEmitter<any> = new EventEmitter();

    @ViewChildren('vc', {read: ViewContainerRef}) viewContainerRefs:
        Array<ViewContainerRef>;

    fields: Array<any>;

    protected controlTypes: Array<ControlType> = [];
    removeMode: boolean = false;

    constructor(
        protected elemRef: ElementRef,
        protected appFacade: VasAppFacade,
        protected componentFactoryResolver: ComponentFactoryResolver
    ) {
        super(elemRef);
    }

    ngOnInit(): void {

        if (!this.form) {
            throw new Error('@Input form is required');
        }

        super.ngOnInit();

        this.appFacade.objects.controlType.store
            .getAll()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(controlTypes => this.controlTypes = controlTypes);

        let fg;

        if (!this.value) {
            if (
                isDefined(this.definition.defaultValue) &&
                this.definition.defaultValue
            ) {
                this.updateValue(this.definition.defaultValue);
            } else {
                const limit = parseInt(this.getOption('initRowCount', '0'), 10);
                for (let i = 0; i < limit; i++) {
                    fg = this.control ?
                        this.control.controls[i] as FormGroup :
                        undefined;
                    this.onNewRow(fg);
                }
            }
        }

        this.parseOptions();

        setTimeout(() => {
            this.createFields();
        }, 10);
    }

    ngOnDestroy(): void {
        this.viewContainerRefs.forEach(container => {
            container.clear();
        });
    }

    createFields(): void {
        this.viewContainerRefs.forEach(
            (container: ViewContainerRef, i: number) => {

                // check if this viewContainer already has has the
                // FieldWrapperComponent inserted, if so skip it
                if (container.element.nativeElement.nextElementSibling) {
                    return;
                }
                try {
                    const rowIndex = Math.floor(
                        i / this.definition.config.controls.length
                    );
                    const colIndex = i % this.definition.config.controls.length;
                    container.clear();
                    insertComponentDynamic(
                        FieldWrapperComponent,
                        this.componentFactoryResolver,
                        container,
                        {
                            formGroup: this.formGroup,
                            form: this.form,
                            xformControl: this.getFormControlByRowCol(
                                rowIndex,
                                colIndex
                            ),
                            definition: this.getControlByIndex(colIndex)
                        } as IFieldWrapperComponentInputs
                    );
                } catch (e) {
                    console.error(e);
                }
            });
    }

    getFormControlByRowCol(row, col): FormControl {
        const fg = (this.control.controls[row] as FormGroup);
        const keys = Object.keys(fg.controls);
        return fg.controls[keys[col]] as FormControl;
    }

    parseOptions(): void {

        const showHeaders = this.getOption('showHeaders', true);
        const showLabels = this.getOption('showLabels', true);

        if (showHeaders && showLabels) {
            console.warn(
                `invalid options for field ${this.definition.name}.` +
                'showHeaders and showLabels cannot both be true'
            );
        }

    }

    getControlByIndex(index: number): ControlInlined {
        const control = this.definition.config.controls[index];

        return new ControlInlined({
            ...control,
            controlType: this.getControlType(control.controlType)
        });
    }

    getControlType(id: string): ControlType {
        return this.controlTypes.find(
            controlType => controlType.id === id || controlType.name === id
        );
    }

    onNewRow(group?: FormGroup): void {

        const rowGroup = {};

        if (!group) {
            this.definition.config.controls.forEach((def: Control) => {
                rowGroup[def.name] = new FormControl();
            });
            this.control.push(new FormGroup(rowGroup));
        }

        this.updateValue(this.control.value);

        setTimeout(() => {
            this.createFields();
        }, 10);
    }

    onRemoveRow(i): void {
        this.control.removeAt(i);
        this.updateValue(this.control.value);
        this.control.markAsDirty();
    }

    onRemoveMode(): void {
        this.removeMode = !this.removeMode;
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/complex/form/complex-control.component.ts